import {
  FolderClosed,
  FolderInput,
  FolderMinus,
  FolderOpen,
  Image as ImageIcon,
  Lightbulb,
  Plus,
  Sparkles,
  Sun,
  Trash2,
  User,
} from "lucide-react";
import type { MouseEvent as ReactMouseEvent, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { findEntryByObjectName } from "../app/blendFileQueries.ts";
import { useStore } from "../app/store.ts";
import type { Collection } from "../app/storeTypes.ts";
import { useSelectedLeafObjectIds } from "../app/useSelectedLeafObjectIds.ts";
import { ObjectType } from "../blend-reader/types/BlendObject.ts";
import { storeKeyToEntityId, useEntityRegistry } from "../ecs/index.ts";
import type { SceneObject } from "../modeling/types.ts";
import { isLightObject } from "../modeling/types.ts";
import { hasTouchScreen } from "../viewport/interactionConstants.ts";
import { Button, IconButton } from "./Button.tsx";
import { DraggablePanel } from "./DraggablePanel.tsx";
import {
  ArmatureIcon,
  EmptyIcon,
  EyeClosedIcon,
  EyeOpenIcon,
  MeshIcon,
  SceneIcon,
} from "./Icons.tsx";
import { MenuItem } from "./menu/MenuItem.tsx";
import { MenuDivider } from "./menu/MenuPrimitives.tsx";
import { Popover } from "./Popover.tsx";
import { CollapsibleTreeRow, SearchField } from "./shared.tsx";
import { GLASS_PANEL } from "./styles.ts";
import {
  COLOR_ACCENT,
  COLOR_ARMATURE,
  COLOR_BORDER_STRONG,
  COLOR_GOLD,
  COLOR_SURFACE,
  COLOR_TEXT,
  COLOR_TEXT_DIM,
  COLOR_TEXT_DISABLED,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_SECONDARY,
  FONT_SIZE_BODY,
  FONT_SIZE_SMALL,
  FONT_SIZE_TINY,
  PANEL_SIDE_INSET,
  PANEL_TOP_OFFSET,
  RADIUS_CONTROL,
  RADIUS_SM,
  Z_FLOATING,
} from "./tokens.ts";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function typeIcon(type: ObjectType): { icon: ReactNode; cls: string } {
  switch (type) {
    case ObjectType.Mesh:
      return { icon: <MeshIcon />, cls: COLOR_ACCENT };
    case ObjectType.Armature:
      return { icon: <ArmatureIcon />, cls: COLOR_ARMATURE };
    default:
      return { icon: <EmptyIcon />, cls: COLOR_TEXT_DIM };
  }
}

function isObjectVisible(
  visibility: ReadonlyMap<string, boolean>,
  objectId: string,
): boolean {
  return visibility.get(objectId) ?? true;
}

function HierarchyVisibilityButton({
  visible,
  onClick,
  color,
  title,
}: {
  visible: boolean;
  onClick: (e: ReactMouseEvent) => void;
  color?: string;
  title: string;
}) {
  return (
    <span
      onClick={onClick}
      title={title}
      style={{
        padding: "2px 4px",
        cursor: "pointer",
        flexShrink: 0,
        color: color ?? (visible ? COLOR_TEXT : COLOR_TEXT_DISABLED),
        display: "flex",
        alignItems: "center",
      }}
    >
      {visible ? <EyeOpenIcon /> : <EyeClosedIcon />}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Context menu state
// ---------------------------------------------------------------------------

interface ContextMenuState {
  objectId: string;
  x: number;
  y: number;
}

const Divider = MenuDivider;

// ---------------------------------------------------------------------------
// Context menu
// ---------------------------------------------------------------------------

function HierarchyContextMenu({
  menu,
  onClose,
}: {
  menu: ContextMenuState;
  onClose: () => void;
}) {
  const collections = useStore((s) => s.collections);
  const selectedObjects = useSelectedLeafObjectIds();
  const moveToCollection = useStore((s) => s.moveToCollection);
  const removeFromCollection = useStore((s) => s.removeFromCollection);
  const createCollection = useStore((s) => s.createCollection);
  const blendFiles = useStore((s) => s.blendFiles);
  const removeBlendFile = useStore((s) => s.removeBlendFile);

  // Operate on the full selection if the right-clicked object is part of it,
  // otherwise just the single right-clicked object.
  const targetIds = useMemo(() => {
    if (selectedObjects.has(menu.objectId) && selectedObjects.size > 1) {
      return [...selectedObjects];
    }
    return [menu.objectId];
  }, [selectedObjects, menu.objectId]);

  // Does the right-clicked object belong to a blend file entry?
  const blendEntry = useMemo(() => {
    return findEntryByObjectName(blendFiles, menu.objectId);
  }, [blendFiles, menu.objectId]);

  // Which collection(s) do the targets belong to?
  const memberOfCollection = useMemo(() => {
    for (const [, coll] of collections) {
      if (targetIds.some((id) => coll.objectIds.has(id))) return coll;
    }
    return null;
  }, [collections, targetIds]);

  const [subMenu, setSubMenu] = useState<"move" | null>(null);
  const [newCollName, setNewCollName] = useState("");
  const newCollRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (subMenu === "move" && newCollRef.current) {
      setTimeout(() => newCollRef.current?.focus(), 0);
    }
  }, [subMenu]);

  const label = targetIds.length > 1 ? `${targetIds.length} objects` : "object";

  if (subMenu === "move") {
    const collList = [...collections.values()];
    return (
      <Popover open anchorPoint={{ x: menu.x, y: menu.y }} onClose={onClose}>
        <MenuItem label={"\u2039 Back"} onClick={() => setSubMenu(null)} />
        <Divider />
        {collList.map((coll) => (
          <MenuItem
            key={coll.id}
            label={coll.name}
            onClick={() => {
              moveToCollection(coll.id, targetIds);
              onClose();
            }}
          />
        ))}
        <Divider />
        <div style={{ display: "flex", padding: "4px 8px", gap: 4 }}>
          <input
            ref={newCollRef}
            type="text"
            placeholder="New Collection"
            value={newCollName}
            onChange={(e) => setNewCollName(e.target.value)}
            onKeyDown={(e) => {
              if (!(e.metaKey || e.ctrlKey)) e.stopPropagation();
              if (e.key === "Enter" && newCollName.trim()) {
                const id = createCollection(newCollName.trim());
                moveToCollection(id, targetIds);
                onClose();
              }
              if (e.key === "Escape") onClose();
            }}
            style={{
              flex: 1,
              padding: "4px 8px",
              borderRadius: RADIUS_CONTROL,
              border: `1px solid ${COLOR_BORDER_STRONG}`,
              background: COLOR_SURFACE,
              color: COLOR_TEXT_SECONDARY,
              fontSize: FONT_SIZE_SMALL,
              outline: "none",
            }}
          />
          <IconButton
            onClick={() => {
              if (newCollName.trim()) {
                const id = createCollection(newCollName.trim());
                moveToCollection(id, targetIds);
                onClose();
              }
            }}
            icon={<Plus size={12} />}
            label="Create collection"
            variant="primary"
            size={24}
          />
        </div>
      </Popover>
    );
  }

  return (
    <Popover open anchorPoint={{ x: menu.x, y: menu.y }} onClose={onClose}>
      <MenuItem
        label={`Move ${label} to Collection`}
        shortcutKey="m"
        onClick={() => setSubMenu("move")}
      />
      {memberOfCollection && (
        <MenuItem
          label={`Remove ${label} from "${memberOfCollection.name}"`}
          onClick={() => {
            removeFromCollection(memberOfCollection.id, targetIds);
            onClose();
          }}
        />
      )}
      {blendEntry && (
        <>
          <Divider />
          <MenuItem
            label={`Remove "${blendEntry[1].fileName}"`}
            onClick={() => {
              removeBlendFile(blendEntry[0]);
              onClose();
            }}
          />
        </>
      )}
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Collection item
// ---------------------------------------------------------------------------

const COLOR_COLLECTION = "#a0b8d8";

function CollectionItem({
  collection,
  filter,
  onContextMenu,
}: {
  collection: Collection;
  filter: string;
  onContextMenu: (objectId: string, x: number, y: number) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const [isRenaming, setIsRenaming] = useState(false);
  const [editValue, setEditValue] = useState(collection.name);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedCollectionId = useStore((s) => {
    if (s.selectedObjectTargets.length !== 1) return null;
    const target = s.selectedObjectTargets[0];
    return target?.kind === "collection" ? target.id : null;
  });
  const selectCollection = useStore((s) => s.selectCollection);
  const toggleCollectionVisibility = useStore(
    (s) => s.toggleCollectionVisibility,
  );
  const renameCollection = useStore((s) => s.renameCollection);
  const deleteCollection = useStore((s) => s.deleteCollection);
  const visibility = useStore((s) => s.visibility);
  const registry = useEntityRegistry();

  const isSelected = selectedCollectionId === collection.id;

  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  const commitRename = useCallback(() => {
    setIsRenaming(false);
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== collection.name) {
      renameCollection(collection.id, trimmed);
    } else {
      setEditValue(collection.name);
    }
  }, [editValue, collection.name, collection.id, renameCollection]);

  // Gather member objects for rendering (via ECS — no dual-lookup)
  const members = useMemo(() => {
    const result: Array<{
      id: string;
      name: string;
      icon: ReactNode;
      iconColor: string;
      kind: "blend" | "scene";
    }> = [];
    for (const objId of collection.objectIds) {
      const entity = registry.get(storeKeyToEntityId(objId));
      if (!entity) continue;

      let iconEl: ReactNode;
      let iconColor: string;
      if (entity.origin === "blend") {
        // Use the blend type icon helper for blend objects
        const blendType =
          entity.kind === "mesh"
            ? ObjectType.Mesh
            : entity.kind === "armature"
              ? ObjectType.Armature
              : ObjectType.Empty;
        const { icon, cls } = typeIcon(blendType);
        iconEl = icon;
        iconColor = cls;
      } else {
        iconEl =
          entity.kind === "light" ? (
            entity.components.light?.lightKind === "directional" ? (
              <Sun size={13} />
            ) : (
              <Lightbulb size={13} />
            )
          ) : entity.kind === "mannequin" ? (
            <User size={13} />
          ) : (
            <MeshIcon />
          );
        iconColor = entity.kind === "light" ? COLOR_GOLD : COLOR_ACCENT;
      }
      result.push({
        id: objId,
        name: entity.name,
        icon: iconEl,
        iconColor,
        kind: entity.origin,
      });
    }
    return result;
  }, [collection.objectIds, registry]);

  // Filter check
  const nameMatches = !filter || collection.name.toLowerCase().includes(filter);
  const memberMatches =
    !filter || members.some((m) => m.name.toLowerCase().includes(filter));
  if (!nameMatches && !memberMatches) return null;

  const isVisible = [...collection.objectIds].some((objectId) =>
    isObjectVisible(visibility, objectId),
  );
  const isGeneratorBacked = collection.generatorRef !== undefined;
  const iconColor = isGeneratorBacked ? COLOR_ACCENT : COLOR_COLLECTION;
  const showChildren = !isGeneratorBacked && expanded;

  const leadingIcon = isGeneratorBacked ? (
    <Sparkles size={13} />
  ) : expanded ? (
    <FolderOpen size={13} />
  ) : (
    <FolderClosed size={13} />
  );

  const titleNode = isRenaming ? (
    <input
      ref={inputRef}
      type="text"
      value={editValue}
      onChange={(e) => setEditValue(e.target.value)}
      onBlur={commitRename}
      onKeyDown={(e) => {
        if (!(e.metaKey || e.ctrlKey)) e.stopPropagation();
        if (e.key === "Enter") commitRename();
        if (e.key === "Escape") {
          setEditValue(collection.name);
          setIsRenaming(false);
        }
      }}
      onClick={(e) => e.stopPropagation()}
      style={{
        flex: 1,
        minWidth: 0,
        padding: "1px 4px",
        borderRadius: RADIUS_SM,
        border: `1px solid ${COLOR_ACCENT}`,
        background: COLOR_SURFACE,
        color: COLOR_TEXT,
        fontSize: FONT_SIZE_BODY + 1,
        outline: "none",
      }}
    />
  ) : (
    collection.name
  );

  return (
    <>
      <CollapsibleTreeRow
        title={titleNode}
        selected={isSelected}
        onSelect={() => {
          if (!isRenaming) selectCollection(collection.id);
        }}
        onTitleDoubleClick={(e) => {
          e.stopPropagation();
          setEditValue(collection.name);
          setIsRenaming(true);
        }}
        // Generator collections aren't expandable — hide the chevron by
        // passing undefined toggle state. The plan tracks selection separately.
        isCollapsed={isGeneratorBacked ? undefined : !expanded}
        onToggleCollapse={
          isGeneratorBacked ? undefined : () => setExpanded(!expanded)
        }
        leadingIcon={leadingIcon}
        leadingIconColor={iconColor}
        actions={
          <>
            <span
              style={{
                fontSize: FONT_SIZE_TINY,
                color: COLOR_TEXT_FAINT,
                flexShrink: 0,
              }}
            >
              {collection.objectIds.size}
            </span>
            <HierarchyVisibilityButton
              visible={isVisible}
              onClick={(e) => {
                e.stopPropagation();
                toggleCollectionVisibility(collection.id);
              }}
              color={isVisible ? COLOR_COLLECTION : COLOR_TEXT_DISABLED}
              title={isVisible ? "Hide collection" : "Show collection"}
            />
            <span
              onClick={(e) => {
                e.stopPropagation();
                deleteCollection(collection.id);
              }}
              title="Delete collection"
              style={{
                padding: "2px 4px",
                cursor: "pointer",
                flexShrink: 0,
                color: COLOR_TEXT_FAINT,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Trash2 size={12} />
            </span>
          </>
        }
      />

      {/* Children */}
      {showChildren &&
        members
          .filter((m) => !filter || m.name.toLowerCase().includes(filter))
          .map((m) => (
            <CollectionMemberItem
              key={m.id}
              member={m}
              onContextMenu={onContextMenu}
            />
          ))}
    </>
  );
}

function CollectionMemberItem({
  member,
  onContextMenu,
}: {
  member: { id: string; name: string; icon: ReactNode; iconColor: string };
  onContextMenu: (objectId: string, x: number, y: number) => void;
}) {
  const selected = useSelectedLeafObjectIds().has(member.id);
  const selectObject = useStore((s) => s.selectObject);
  const toggleVisibility = useStore((s) => s.toggleVisibility);
  const visibility = useStore((s) => s.visibility);
  const visible = isObjectVisible(visibility, member.id);

  return (
    <CollapsibleTreeRow
      title={member.name}
      selected={selected}
      dim={!visible}
      muted
      emphasizeTitle={false}
      indent={36}
      style={{ padding: "6px 10px", paddingLeft: 46 }}
      leadingIcon={member.icon}
      leadingIconColor={visible ? member.iconColor : COLOR_TEXT_DISABLED}
      onSelect={(e) => selectObject(member.id, e.shiftKey)}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onContextMenu(member.id, e.clientX, e.clientY);
      }}
      actions={
        <HierarchyVisibilityButton
          visible={visible}
          onClick={(e) => {
            e.stopPropagation();
            toggleVisibility(member.id);
          }}
          title={visible ? "Hide object" : "Show object"}
        />
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Scene object items
// ---------------------------------------------------------------------------

function SceneObjectItem({
  obj,
  onContextMenu,
}: {
  obj: SceneObject;
  onContextMenu: (objectId: string, x: number, y: number) => void;
}) {
  const selected = useSelectedLeafObjectIds().has(obj.id);
  const selectObject = useStore((s) => s.selectObject);
  const removeSceneObject = useStore((s) => s.removeSceneObject);
  const renameSceneObject = useStore((s) => s.renameSceneObject);
  const pushUndo = useStore((s) => s.pushUndo);
  const toggleVisibility = useStore((s) => s.toggleVisibility);
  const visibility = useStore((s) => s.visibility);
  const [isRenaming, setIsRenaming] = useState(false);
  const [editValue, setEditValue] = useState(obj.name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  const commitRename = useCallback(() => {
    setIsRenaming(false);
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== obj.name) {
      renameSceneObject(obj.id, trimmed);
    } else {
      setEditValue(obj.name);
    }
  }, [editValue, obj.name, obj.id, renameSceneObject]);

  const visible = isObjectVisible(visibility, obj.id);

  const leadingIcon =
    obj.objectKind === "point_light" ? (
      <Lightbulb size={13} />
    ) : obj.objectKind === "directional_light" ? (
      <Sun size={13} />
    ) : obj.objectKind === "mannequin" ? (
      <User size={13} />
    ) : obj.objectKind === "image_plane" ? (
      <ImageIcon size={13} />
    ) : (
      <MeshIcon />
    );

  const titleNode = isRenaming ? (
    <input
      ref={inputRef}
      type="text"
      value={editValue}
      onChange={(e) => setEditValue(e.target.value)}
      onBlur={commitRename}
      onKeyDown={(e) => {
        if (!(e.metaKey || e.ctrlKey)) e.stopPropagation();
        if (e.key === "Enter") commitRename();
        if (e.key === "Escape") {
          setEditValue(obj.name);
          setIsRenaming(false);
        }
      }}
      onClick={(e) => e.stopPropagation()}
      style={{
        flex: 1,
        minWidth: 0,
        padding: "1px 4px",
        borderRadius: RADIUS_SM,
        border: `1px solid ${COLOR_ACCENT}`,
        background: COLOR_SURFACE,
        color: COLOR_TEXT,
        fontSize: FONT_SIZE_BODY + 1,
        outline: "none",
      }}
    />
  ) : (
    obj.name
  );

  return (
    <CollapsibleTreeRow
      title={titleNode}
      selected={selected}
      dim={!visible}
      indent={18}
      leadingIcon={leadingIcon}
      leadingIconColor={isLightObject(obj) ? COLOR_GOLD : COLOR_ACCENT}
      onSelect={(e) => {
        if (!isRenaming) selectObject(obj.id, e.shiftKey);
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onContextMenu(obj.id, e.clientX, e.clientY);
      }}
      onTitleDoubleClick={(e) => {
        e.stopPropagation();
        setEditValue(obj.name);
        setIsRenaming(true);
      }}
      actions={
        <>
          <HierarchyVisibilityButton
            visible={visible}
            onClick={(e) => {
              e.stopPropagation();
              toggleVisibility(obj.id);
            }}
            title={visible ? "Hide object" : "Show object"}
          />
          <span
            onClick={(e) => {
              e.stopPropagation();
              pushUndo();
              removeSceneObject(obj.id);
            }}
            title="Delete object"
            style={{
              padding: "2px 4px",
              cursor: "pointer",
              flexShrink: 0,
              color: COLOR_TEXT_FAINT,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Trash2 size={13} />
          </span>
        </>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Selection action toolbar (touch-friendly alternative to context menu)
// ---------------------------------------------------------------------------

function SelectionToolbar() {
  const selectedObjects = useSelectedLeafObjectIds();
  const collections = useStore((s) => s.collections);
  const moveToCollection = useStore((s) => s.moveToCollection);
  const removeFromCollection = useStore((s) => s.removeFromCollection);
  const createCollection = useStore((s) => s.createCollection);
  const removeSceneObject = useStore((s) => s.removeSceneObject);
  const toggleVisibility = useStore((s) => s.toggleVisibility);
  const pushUndo = useStore((s) => s.pushUndo);
  const blendFiles = useStore((s) => s.blendFiles);
  const sceneObjects = useStore((s) => s.sceneObjects);

  const [moveOpen, setMoveOpen] = useState(false);
  const [newCollName, setNewCollName] = useState("");
  const moveRef = useRef<HTMLButtonElement>(null);
  const newCollRef = useRef<HTMLInputElement>(null);

  const targetIds = useMemo(() => [...selectedObjects], [selectedObjects]);

  // Which collection do the selected objects belong to (if any)?
  const memberOfCollection = useMemo(() => {
    for (const [, coll] of collections) {
      if (targetIds.some((id) => coll.objectIds.has(id))) return coll;
    }
    return null;
  }, [collections, targetIds]);

  // Can we delete? Only scene objects can be deleted; blend objects get hidden.
  const hasSceneObjects = useMemo(
    () => targetIds.some((id) => sceneObjects.has(id)),
    [targetIds, sceneObjects],
  );
  const hasBlendObjects = useMemo(() => {
    if (blendFiles.size === 0) return false;
    const blendNames = new Set<string>();
    for (const entry of blendFiles.values()) {
      for (const obj of entry.file.objects) blendNames.add(obj.name);
    }
    return targetIds.some((id) => blendNames.has(id));
  }, [targetIds, blendFiles]);

  useEffect(() => {
    if (moveOpen && newCollRef.current) {
      setTimeout(() => newCollRef.current?.focus(), 0);
    }
  }, [moveOpen]);

  if (targetIds.length === 0) return null;

  const handleDelete = () => {
    pushUndo();
    for (const id of targetIds) {
      if (sceneObjects.has(id)) {
        removeSceneObject(id);
      } else {
        toggleVisibility(id);
      }
    }
  };

  const collList = [...collections.values()];

  const anchorRect = moveRef.current?.getBoundingClientRect();

  return (
    <div
      style={{
        display: "flex",
        gap: 4,
        padding: "0 10px 6px",
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      <span
        style={{
          fontSize: FONT_SIZE_TINY,
          color: COLOR_TEXT_FAINT,
          marginRight: 2,
        }}
      >
        {targetIds.length} selected
      </span>

      {/* Move to collection */}
      <Button
        ref={moveRef}
        onClick={() => setMoveOpen(!moveOpen)}
        variant="secondary"
        size="xs"
        leftIcon={<FolderInput size={12} />}
      >
        Collect
      </Button>

      {/* Remove from collection */}
      {memberOfCollection && (
        <Button
          onClick={() => removeFromCollection(memberOfCollection.id, targetIds)}
          variant="secondary"
          size="xs"
          leftIcon={<FolderMinus size={12} />}
        >
          Uncollect
        </Button>
      )}

      {/* Delete / Hide */}
      <Button
        onClick={handleDelete}
        title={
          hasBlendObjects && !hasSceneObjects
            ? "Hide selected"
            : "Delete selected"
        }
        variant="secondary"
        size="xs"
        leftIcon={<Trash2 size={12} />}
        style={{ color: COLOR_TEXT_DIM }}
      >
        {hasBlendObjects && !hasSceneObjects ? "Hide" : "Delete"}
      </Button>

      {/* Move-to-collection popover */}
      {moveOpen && anchorRect && (
        <Popover
          open
          anchorPoint={{ x: anchorRect.left, y: anchorRect.bottom + 4 }}
          onClose={() => {
            setMoveOpen(false);
            setNewCollName("");
          }}
        >
          {collList.map((coll) => (
            <MenuItem
              key={coll.id}
              label={coll.name}
              onClick={() => {
                moveToCollection(coll.id, targetIds);
                setMoveOpen(false);
              }}
            />
          ))}
          {collList.length > 0 && <Divider />}
          <div style={{ display: "flex", padding: "4px 8px", gap: 4 }}>
            <input
              ref={newCollRef}
              type="text"
              placeholder="New Collection"
              value={newCollName}
              onChange={(e) => setNewCollName(e.target.value)}
              onKeyDown={(e) => {
                if (!(e.metaKey || e.ctrlKey)) e.stopPropagation();
                if (e.key === "Enter" && newCollName.trim()) {
                  const id = createCollection(newCollName.trim());
                  moveToCollection(id, targetIds);
                  setMoveOpen(false);
                  setNewCollName("");
                }
                if (e.key === "Escape") {
                  setMoveOpen(false);
                  setNewCollName("");
                }
              }}
              style={{
                flex: 1,
                padding: "4px 8px",
                borderRadius: RADIUS_CONTROL,
                border: `1px solid ${COLOR_BORDER_STRONG}`,
                background: COLOR_SURFACE,
                color: COLOR_TEXT_SECONDARY,
                fontSize: FONT_SIZE_SMALL,
                outline: "none",
              }}
            />
            <IconButton
              onClick={() => {
                if (newCollName.trim()) {
                  const id = createCollection(newCollName.trim());
                  moveToCollection(id, targetIds);
                  setMoveOpen(false);
                  setNewCollName("");
                }
              }}
              icon={<Plus size={12} />}
              label="Create collection"
              variant="primary"
              size={24}
            />
          </div>
        </Popover>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Blend file entry — monolithic row (not expandable)
// ---------------------------------------------------------------------------

function BlendFileGroup({
  entryId,
  fileName,
  objectNames,
  filter,
}: {
  entryId: string;
  fileName: string;
  objectNames: string[];
  filter: string;
}) {
  const removeBlendFile = useStore((s) => s.removeBlendFile);
  const selectBlendEntry = useStore((s) => s.selectBlendEntry);
  const selectedObjects = useSelectedLeafObjectIds();

  // Hide when no object names match the search filter
  const matches = useMemo(
    () =>
      !filter ||
      objectNames.some((n) => n.toLowerCase().includes(filter)) ||
      fileName.toLowerCase().includes(filter),
    [objectNames, fileName, filter],
  );

  const isSelected = useMemo(
    () =>
      objectNames.length > 0 &&
      objectNames.every((n) => selectedObjects.has(n)),
    [objectNames, selectedObjects],
  );

  if (!matches) return null;

  return (
    <CollapsibleTreeRow
      title={fileName}
      selected={isSelected}
      indent={18}
      leadingIcon={<SceneIcon />}
      onSelect={() => selectBlendEntry(entryId)}
      actions={
        <>
          <span
            style={{
              fontSize: FONT_SIZE_TINY,
              color: COLOR_TEXT_FAINT,
              flexShrink: 0,
            }}
          >
            {objectNames.length}
          </span>
          <span
            onClick={(e) => {
              e.stopPropagation();
              removeBlendFile(entryId);
            }}
            title={`Remove ${fileName}`}
            style={{
              padding: "2px 4px",
              cursor: "pointer",
              flexShrink: 0,
              color: COLOR_TEXT_FAINT,
              display: "flex",
              alignItems: "center",
            }}
          >
            <Trash2 size={13} />
          </span>
        </>
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

/**
 * Standalone body content for use inside the floating panel system.
 * This is the same hierarchy UI without the DraggablePanel wrapper.
 */
export function HierarchyPanelBody() {
  const blendFiles = useStore((s) => s.blendFiles);
  const sceneObjects = useStore((s) => s.sceneObjects);
  const collections = useStore((s) => s.collections);
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const filter = search.toLowerCase();

  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);

  const handleContextMenu = useCallback(
    (objectId: string, x: number, y: number) => {
      setContextMenu({ objectId, x, y });
    },
    [],
  );

  const perEntryInfo = useMemo(() => {
    const result: Array<{
      entryId: string;
      fileName: string;
      objectNames: string[];
    }> = [];
    for (const [entryId, entry] of blendFiles) {
      result.push({
        entryId,
        fileName: entry.fileName,
        objectNames: entry.file.objects.map((o) => o.name),
      });
    }
    return result;
  }, [blendFiles]);

  const collectedObjectIds = useMemo(() => {
    const set = new Set<string>();
    for (const [, coll] of collections) {
      for (const id of coll.objectIds) set.add(id);
    }
    return set;
  }, [collections]);

  const uncollectedScene = useMemo(
    () =>
      [...sceneObjects.values()].filter((o) => !collectedObjectIds.has(o.id)),
    [sceneObjects, collectedObjectIds],
  );

  const collectionList = useMemo(
    () => [...collections.values()],
    [collections],
  );

  return (
    <>
      {/* Search */}
      <div style={{ padding: "0 0 6px" }}>
        <SearchField
          value={search}
          onChange={setSearch}
          placeholder="Search…"
          inputRef={searchRef}
          autoFocus={!hasTouchScreen()}
        />
      </div>

      {/* Selection action toolbar */}
      <SelectionToolbar />

      {/* Content */}
      <div
        style={{
          padding: "8px 0",
          maxHeight: 400,
          overflowY: "auto",
          margin: "0 -8px",
        }}
      >
        {/* Collections */}
        {collectionList.map((coll) => (
          <CollectionItem
            key={coll.id}
            collection={coll}
            filter={filter}
            onContextMenu={handleContextMenu}
          />
        ))}

        {/* Blend file entries (monolithic — not expandable) */}
        {perEntryInfo.map(({ entryId, fileName, objectNames }) => (
          <BlendFileGroup
            key={entryId}
            entryId={entryId}
            fileName={fileName}
            objectNames={objectNames}
            filter={filter}
          />
        ))}

        {/* Uncollected scene objects */}
        {uncollectedScene
          .filter((o) => !filter || o.name.toLowerCase().includes(filter))
          .map((obj) => (
            <SceneObjectItem
              key={obj.id}
              obj={obj}
              onContextMenu={handleContextMenu}
            />
          ))}

        {blendFiles.size === 0 && sceneObjects.size === 0 && (
          <div
            style={{
              padding: "24px 16px",
              textAlign: "center",
              color: COLOR_TEXT_DIM,
              fontSize: FONT_SIZE_BODY,
              lineHeight: 1.5,
            }}
          >
            No objects in scene.
            <br />
            <span
              style={{ fontSize: FONT_SIZE_SMALL, color: COLOR_TEXT_FAINT }}
            >
              Drop a .blend file or add an object to get started.
            </span>
          </div>
        )}
      </div>

      {/* Context menu */}
      {contextMenu && (
        <HierarchyContextMenu
          menu={contextMenu}
          onClose={() => setContextMenu(null)}
        />
      )}
    </>
  );
}

export function HierarchyPanel({ onClose }: { onClose?: () => void }) {
  const blendFiles = useStore((s) => s.blendFiles);
  const sceneObjects = useStore((s) => s.sceneObjects);
  const collections = useStore((s) => s.collections);
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const filter = search.toLowerCase();

  const [contextMenu, setContextMenu] = useState<ContextMenuState | null>(null);

  const handleContextMenu = useCallback(
    (objectId: string, x: number, y: number) => {
      setContextMenu({ objectId, x, y });
    },
    [],
  );

  // Build per-entry object name lists for monolithic blend file rows
  const perEntryInfo = useMemo(() => {
    const result: Array<{
      entryId: string;
      fileName: string;
      objectNames: string[];
    }> = [];
    for (const [entryId, entry] of blendFiles) {
      result.push({
        entryId,
        fileName: entry.fileName,
        objectNames: entry.file.objects.map((o) => o.name),
      });
    }
    return result;
  }, [blendFiles]);

  // Compute which objects are in any collection (to exclude from uncollected list)
  const collectedObjectIds = useMemo(() => {
    const set = new Set<string>();
    for (const [, coll] of collections) {
      for (const id of coll.objectIds) set.add(id);
    }
    return set;
  }, [collections]);

  // Uncollected scene objects
  const uncollectedScene = useMemo(
    () =>
      [...sceneObjects.values()].filter((o) => !collectedObjectIds.has(o.id)),
    [sceneObjects, collectedObjectIds],
  );

  const collectionList = useMemo(
    () => [...collections.values()],
    [collections],
  );

  const hierarchyHeader = (
    <>
      <span
        style={{
          display: "flex",
          alignItems: "center",
          color: COLOR_TEXT_FAINT,
        }}
      >
        <SceneIcon />
      </span>
      Scene
    </>
  );

  return (
    <DraggablePanel
      id="hierarchy-panel"
      defaultPosition={{ top: PANEL_TOP_OFFSET, right: PANEL_SIDE_INSET }}
      zIndex={Z_FLOATING}
      onClose={onClose}
      collapsible
      header={hierarchyHeader}
      style={{
        width: 340,
        maxHeight: "calc(100% - 120px)",
      }}
    >
      <div
        style={{
          background: GLASS_PANEL.background,
          backdropFilter: GLASS_PANEL.backdropFilter as string,
          WebkitBackdropFilter: GLASS_PANEL.backdropFilter as string,
          borderTop: "none",
          borderRight: GLASS_PANEL.border,
          borderBottom: GLASS_PANEL.border,
          borderLeft: GLASS_PANEL.border,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomLeftRadius: 16,
          borderBottomRightRadius: 16,
        }}
      >
        {/* Search */}
        <div style={{ padding: "4px 10px 6px" }}>
          <SearchField
            value={search}
            onChange={setSearch}
            placeholder="Search…"
            inputRef={searchRef}
            autoFocus={!hasTouchScreen()}
          />
        </div>

        {/* Selection action toolbar */}
        <SelectionToolbar />

        {/* Content */}
        <div
          style={{
            padding: "8px 6px",
            maxHeight: 400,
            overflowY: "auto",
          }}
        >
          {/* Collections */}
          {collectionList.map((coll) => (
            <CollectionItem
              key={coll.id}
              collection={coll}
              filter={filter}
              onContextMenu={handleContextMenu}
            />
          ))}

          {/* Blend file entries (monolithic — not expandable) */}
          {perEntryInfo.map(({ entryId, fileName, objectNames }) => (
            <BlendFileGroup
              key={entryId}
              entryId={entryId}
              fileName={fileName}
              objectNames={objectNames}
              filter={filter}
            />
          ))}

          {/* Uncollected scene objects */}
          {uncollectedScene
            .filter((o) => !filter || o.name.toLowerCase().includes(filter))
            .map((obj) => (
              <SceneObjectItem
                key={obj.id}
                obj={obj}
                onContextMenu={handleContextMenu}
              />
            ))}

          {blendFiles.size === 0 && sceneObjects.size === 0 && (
            <div
              style={{
                padding: "24px 16px",
                textAlign: "center",
                color: COLOR_TEXT_DIM,
                fontSize: FONT_SIZE_BODY,
                lineHeight: 1.5,
              }}
            >
              No objects in scene.
              <br />
              <span
                style={{ fontSize: FONT_SIZE_SMALL, color: COLOR_TEXT_FAINT }}
              >
                Drop a .blend file or add an object to get started.
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Context menu */}
      {contextMenu && (
        <HierarchyContextMenu
          menu={contextMenu}
          onClose={() => setContextMenu(null)}
        />
      )}
    </DraggablePanel>
  );
}
